import { Card, CardContent } from "@/shared/ui/card";

import type { GeoReport } from "../../../api/geoService";
import { HitPill, Kpi, SecNo } from "./shared";
import { platformLabel } from "../../../lib/platformLabel";

/** 06 目标用户群体表现:按受众分组统计提问表现(分母仅实测,basis 如实展示)。 */
export function AudienceTable({ report }: { report: GeoReport }) {
  const stats = report.audience_stats;
  if (!stats?.available || !stats.segments?.length) return null;
  const pillOf = (status: string): { kind: "rec" | "hit" | "no" | "bad"; text: string } => {
    if (status === "持续优化") return { kind: "hit", text: "优于均值" };
    if (status === "风险关注") return { kind: "bad", text: "需关注" };
    if (status === "优先补强") return { kind: "bad", text: "需关注" };
    return { kind: "no", text: status };
  };
  return (
    <section id="geo-sec-audience" className="space-y-2">
      <SecNo no="06" title="目标用户群体表现" desc="按受众分组统计提问表现" />
      <Card>
        <CardContent className="overflow-x-auto p-6">
          <table className="w-full border-collapse text-xs" data-testid="geo-audience-table">
            <thead>
              <tr className="border-b text-left text-2xs text-muted-foreground">
                <th className="px-2 py-1.5 font-semibold">用户群体</th>
                <th className="px-2 py-1.5 font-semibold">回答数</th>
                <th className="px-2 py-1.5 font-semibold">提及率</th>
                <th className="px-2 py-1.5 font-semibold">正向率</th>
                <th className="px-2 py-1.5 font-semibold">负向率</th>
                <th className="px-2 py-1.5 font-semibold">表现</th>
              </tr>
            </thead>
            <tbody>
              {stats.segments.map((s) => {
                const p = pillOf(s.status);
                return (
                  <tr key={s.name} className="border-b last:border-b-0">
                    <td className="px-2 py-1.5 font-semibold">{s.name}</td>
                    <td className="px-2 py-1.5 tabular-nums">{s.valid_answers}</td>
                    <td className="px-2 py-1.5 tabular-nums">{s.mention_rate}%</td>
                    <td className="px-2 py-1.5 tabular-nums">{s.positive_rate}%</td>
                    <td className="px-2 py-1.5 tabular-nums">{s.negative_rate}%</td>
                    <td className="px-2 py-1.5">
                      <HitPill kind={p.kind}>{s.status}</HitPill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {stats.basis && <div className="mt-2 text-2xs text-muted-foreground">{stats.basis}</div>}
        </CardContent>
      </Card>
    </section>
  );
}

/** 07 核心摘要:KPI 行 + 分项计分条(构成项随后端 score_breakdown.components 动态渲染,v2.2 起含品牌认知共 7 项)。 */
export function CoreSummary({ report }: { report: GeoReport }) {
  const bd = report.score_breakdown;
  if (!bd?.components?.length) return null;
  const ov = report.search_overview;
  const pd = report.platform_details;
  const totalPlatforms = (pd?.real_count ?? 0) + (pd?.derived_count ?? 0);
  return (
    <section id="geo-sec-summary" className="space-y-2">
      <SecNo no="07" title="核心摘要" desc="一屏看懂这轮体检 · 分项计分" />
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
            <Kpi
              value={totalPlatforms}
              label={`覆盖 AI 平台(${pd?.real_count ?? 0} 实测 + ${pd?.derived_count ?? 0} 生态)`}
            />
            <Kpi value={ov?.question_count ?? "—"} label="检测问题" />
            <Kpi value={ov?.valid_answers ?? "—"} label="有效回答" />
            <Kpi
              value={
                ov?.valid_answers
                  ? `${Math.round(((ov.mention_hits ?? 0) / ov.valid_answers) * 100)}%`
                  : "—"
              }
              label="品牌提及率"
            />
            <Kpi value={ov?.negative_hits ?? "—"} label="负向命中" negative />
            <Kpi value={ov?.valid_domain_count ?? "—"} label="有效引用域名" />
          </div>
          <div className="mt-4 flex flex-col gap-2" data-testid="geo-score-dims">
            {bd.components.map((c) => (
              <div
                key={c.key}
                className="grid grid-cols-[140px_1fr_64px] items-center gap-2.5 text-xs"
              >
                <span>{c.label}</span>
                <div className="h-[7px] overflow-hidden rounded-full bg-muted">
                  <i
                    className="block h-full rounded-full bg-primary"
                    style={{ width: `${Math.min(100, c.score)}%` }}
                  />
                </div>
                <span className="text-right font-mono text-2xs text-muted-foreground">
                  {c.weighted} / {c.weight}
                </span>
              </div>
            ))}
          </div>
          {bd.denominator_note && (
            <p className="mt-3 text-2xs leading-relaxed text-muted-foreground">
              {bd.denominator_note}
            </p>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

/** 08 提及率图谱:八平台雷达 + 提及环形(纯 SVG,照实习生)。 */
export function MentionCharts({ report }: { report: GeoReport }) {
  const platforms = report.platform_details?.platforms ?? [];
  if (platforms.length < 3) return null;
  const ov = report.search_overview;
  const n = platforms.length;
  const cx = 100;
  const cy = 100;
  const rMax = 80;
  const angle = (i: number) => (Math.PI * 2 * i) / n - Math.PI / 2;
  const pt = (i: number, r: number) =>
    `${(cx + r * Math.cos(angle(i))).toFixed(1)},${(cy + r * Math.sin(angle(i))).toFixed(1)}`;
  const ring = (r: number) => platforms.map((_, i) => pt(i, r)).join(" ");
  const dataPoints = platforms
    .map((p, i) => pt(i, Math.max(6, (p.mention_rate / 100) * rMax)))
    .join(" ");
  const mentionPct = ov?.valid_answers
    ? Math.round(((ov.mention_hits ?? 0) / ov.valid_answers) * 100)
    : 0;
  const dashMention = (mentionPct / 100) * 339;
  return (
    <section id="geo-sec-charts" className="space-y-2">
      <SecNo no="08" title="提及率图谱" desc="八平台雷达 + 总提及环形" />
      <div className="grid gap-3.5 md:grid-cols-2">
        <Card>
          <CardContent className="p-4">
            <h5 className="mb-1.5 flex items-center gap-2 text-xs font-bold">
              平台品牌提及率雷达
              <span className="ml-auto text-2xs font-normal text-muted-foreground">%</span>
            </h5>
            <svg viewBox="0 0 200 200" className="block max-h-[230px] w-full">
              <g fill="none" stroke="hsl(var(--border))" strokeWidth="1">
                {[rMax, rMax * 0.75, rMax * 0.5, rMax * 0.25].map((r) => (
                  <polygon key={r} points={ring(r)} />
                ))}
                {platforms.map((_, i) => (
                  <path key={i} d={`M${cx},${cy}L${pt(i, rMax)}`} />
                ))}
              </g>
              <polygon
                points={dataPoints}
                fill="hsl(var(--primary) / .22)"
                stroke="hsl(var(--primary))"
                strokeWidth="1.8"
              />
              {platforms.map((p, i) => {
                const [x, y] = pt(i, rMax + 12).split(",");
                return (
                  <text
                    key={p.platform}
                    x={x}
                    y={y}
                    textAnchor="middle"
                    className="fill-muted-foreground"
                    style={{ fontSize: 12 }}
                  >
                    {platformLabel(p.label)}
                    {p.provenance === "real" ? "★" : ""}
                  </text>
                );
              })}
            </svg>
            <div className="mt-1 flex flex-wrap gap-3 text-2xs text-muted-foreground">
              <span>★ = 联网实测</span>
              <span>其余为生态推演</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h5 className="mb-1.5 flex items-center gap-2 text-xs font-bold">
              问题覆盖与提及
              <span className="ml-auto text-2xs font-normal text-muted-foreground">实测同口径</span>
            </h5>
            <svg viewBox="0 0 200 200" className="block max-h-[230px] w-full">
              <circle
                cx="100"
                cy="96"
                r="54"
                fill="none"
                stroke="hsl(var(--muted))"
                strokeWidth="17"
              />
              <circle
                cx="100"
                cy="96"
                r="54"
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="17"
                strokeLinecap="round"
                strokeDasharray={`${dashMention} 339`}
                transform="rotate(-90 100 96)"
              />
              <text
                x="100"
                y="90"
                textAnchor="middle"
                className="fill-foreground"
                style={{ fontSize: 19, fontWeight: 800 }}
              >
                {mentionPct}%
              </text>
              <text
                x="100"
                y="106"
                textAnchor="middle"
                className="fill-muted-foreground"
                style={{ fontSize: 12 }}
              >
                品牌提及率
              </text>
            </svg>
            <div className="mt-1 flex flex-wrap gap-3 text-2xs text-muted-foreground">
              <span>
                提及 {ov?.mention_hits ?? 0}/{ov?.valid_answers ?? 0}
              </span>
              <span>正向命中 {ov?.recommend_hits ?? 0}</span>
              <span>负向命中 {ov?.negative_hits ?? 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
